import React, { useEffect, useState, useMemo, forwardRef, useImperativeHandle } from 'react'
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet'
import L from 'leaflet'
import domtoimage from 'dom-to-image-more'
import 'leaflet/dist/leaflet.css'
import {
  MapAutonomousCommunity,
  MapMunicipality,
  MapMunicipalityMetric,
  MapProvince,
  MapProvinceMetric,
  MapSectionMetric,
  MapYear,
  datasetSignature,
} from '../services/map-municipalities.service'
import { MapMetric } from '../services/map-metrics.service'
import { convertToGeoJSON, getGeoJSONBBox } from '../services/geojson-utils'

export type Props = {
  autonomousCommunities: MapAutonomousCommunity[]
  provinces: MapProvince[]
  municipalities: MapMunicipality[]
  metric: MapMetric
  year?: MapYear
  geographicalData: any
  metricData: MapSectionMetric[] | MapMunicipalityMetric[] | MapProvinceMetric[]
  seriesOptions: any
  years: MapYear[]
  handleYearSelection: (year: MapYear) => void
}

export interface LeafletMapRef {
  exportImage: () => void
}

function formatLegendNumber(num: number): string {
  if (num >= 1000000) return (num / 1000000).toLocaleString('es-ES', { maximumFractionDigits: 1 }) + 'M'
  if (num >= 1000) return (num / 1000).toLocaleString('es-ES', { maximumFractionDigits: 1 }) + 'k'
  return num.toLocaleString('es-ES', { maximumFractionDigits: 1 })
}

function getCityName(props: Record<string, any>): string {
  if (props.name) return props.name
  if (props.NOMBRE) return props.NOMBRE
  if (props.nombre) return props.nombre
  if (props.NMMUN) return props.NMMUN
  if (props.PROVINCIA) return props.PROVINCIA
  if (props.provincia) return props.provincia
  return 'Área seleccionada'
}

function MapController({ geojsonData }: { geojsonData: any }) {
  const map = useMap()
  
  useEffect(() => {
    if (geojsonData && geojsonData.features && geojsonData.features.length > 0) {
      const bounds = getGeoJSONBBox(geojsonData)
      if (bounds) {
        map.fitBounds([
          [bounds[1], bounds[0]],
          [bounds[3], bounds[2]]
        ], { padding: [40, 40], animate: true, duration: 1 })
      }
    }
  }, [geojsonData, map])

  return null
}

function MapInstanceHandler({ onMapReady }: { onMapReady: (map: L.Map) => void }) {
  const map = useMap()
  
  useEffect(() => {
    onMapReady(map)
  }, [map, onMapReady])

  return null
}

export const LeafletMap = forwardRef<LeafletMapRef, Props>(
  ({ metric, year, geographicalData, metricData, seriesOptions }, ref) => {
    const [mapInstance, setMapInstance] = useState<L.Map | null>(null)

    // Convert TopoJSON -> GeoJSON and inject metric values
    const geojsonData = useMemo(() => {
      const converted = convertToGeoJSON(geographicalData)
      if (!converted || !Array.isArray(converted.features)) {
        return { type: 'FeatureCollection', features: [] }
      }

      // Build lookup map: id -> value
      const dataMap = new Map<string | number, number>()
      ;(metricData as any[]).forEach((entry: any) => {
        const id = entry.section_id !== undefined ? entry.section_id : entry.municipality_id !== undefined ? entry.municipality_id : entry.province_id
        if (id !== undefined) {
          dataMap.set(id, entry.value)
        }
      })

      const joinBy = seriesOptions && seriesOptions.joinBy
      const geoKey = joinBy && joinBy[0] // e.g. 'CPRO' or 'id'

      converted.features.forEach((feature: any) => {
        let featureId: string | number | undefined
        if (geoKey) {
          featureId = feature.properties && feature.properties[geoKey]
        } else {
          featureId = feature.properties && feature.properties.id !== undefined ? feature.properties.id : feature.id
        }

        const value = featureId !== undefined ? dataMap.get(featureId) : undefined

        feature.properties = {
          ...feature.properties,
          metricValue: value !== undefined ? value : null,
        }
      })

      return converted
    }, [geographicalData, metricData, seriesOptions])

    // Expose export action via ref
    useImperativeHandle(ref, () => ({
      exportImage: async () => {
        if (!mapInstance) return
        
        // We target the container div that wraps both the map and the legend
        const container = document.querySelector('.leaflet-map-wrapper') as HTMLElement
        if (!container) {
          console.error('Leaflet map wrapper not found')
          return
        }

        try {
          const dataUrl = await domtoimage.toPng(container)
          
          const link = document.createElement('a')
          const safeName = metric.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()
          link.download = `mapa-${safeName}-${year?.name || 'desconocido'}.png`
          link.href = dataUrl
          link.click()

        } catch (error) {
          console.error('Error exporting map image:', error)
        }
      },
    }))

    // Style function for GeoJSON
    const style = (feature: any) => {
      const value = feature && feature.properties && feature.properties.metricValue
      const range = metric.range
      const min = range && range[0] !== undefined ? range[0] : 0
      const max = range && range[1] !== undefined ? range[1] : 100

      let fillColor = '#e0e0e0'
      if (value !== null && value !== undefined) {
        let normalized = (value - min) / (max - min)
        if (normalized < 0) normalized = 0
        if (normalized > 1) normalized = 1

        let r, g, b
        if (normalized <= 0.5) {
          const factor = normalized * 2
          // #d32f2f to #fff176
          r = Math.round(211 + (255 - 211) * factor)
          g = Math.round(47 + (241 - 47) * factor)
          b = Math.round(47 + (118 - 47) * factor)
        } else {
          const factor = (normalized - 0.5) * 2
          // #fff176 to #1976d2
          r = Math.round(255 + (25 - 255) * factor)
          g = Math.round(241 + (118 - 241) * factor)
          b = Math.round(118 + (210 - 118) * factor)
        }
        fillColor = `rgb(${r}, ${g}, ${b})`
      }

      return {
        fillColor,
        weight: 1,
        opacity: 1,
        color: '#999999',
        fillOpacity: 0.85,
      }
    }

    // On each feature
    const onEachFeature = (feature: any, layer: L.Layer) => {
      const props = feature.properties
      const cityName = getCityName(props)
      const value = props.metricValue

      const district = props.district || props.DISTRITO || props.CUDIS
      const section = props.section || props.SECCION || props.CUSEC

      let popupContent = `<div style="font-size:13px;line-height:1.4;">`
      
      // 1. Indicador
      popupContent += `<div style="font-size:11px;color:#777;text-transform:uppercase;margin-bottom:4px;">${metric.name}</div>`
      
      // 2. Ciudad/Entidad
      popupContent += `<div style="font-weight:600;font-size:14px;margin-bottom:2px;color:#333;">${cityName}</div>`
      
      // 3. Dato y unidad
      if (value !== null && value !== undefined) {
        popupContent += `<div style="font-size:15px;color:#1976d2;margin-bottom:6px;font-weight:bold;">${Number(value).toLocaleString('es-ES', { maximumFractionDigits: 2 })} <span style="font-size:12px;color:#555;font-weight:normal;">${metric.unit}</span></div>`
      } else {
        popupContent += `<div style="color:#999;font-style:italic;margin-bottom:6px;">Sin datos</div>`
      }
      
      // 4. Distrito y sección
      if (district && section) {
        popupContent += `<div style="font-size:11px;color:#888;border-top:1px solid #eee;padding-top:4px;margin-top:2px;">Distrito ${district} &bull; Sección ${section}</div>`
      }
      
      popupContent += `</div>`

      layer.bindPopup(popupContent, { closeButton: false, autoPan: false })
      
      let hoverTimeout: number | undefined

      layer.on('mouseover', function(this: L.Path, e: any) {
        this.setStyle({ weight: 2, color: '#333333' })
        
        // Iniciamos el temporizador de 1 segundo (1000 ms)
        hoverTimeout = window.setTimeout(() => {
          layer.openPopup(e.latlng)
        }, 1000)
      })
      
      layer.on('mouseout', function(this: L.Path) {
        this.setStyle({ weight: 1, color: '#999999' })
        
        // Si sale antes de 1 segundo, cancelamos el popup
        if (hoverTimeout !== undefined) {
          window.clearTimeout(hoverTimeout)
          hoverTimeout = undefined
        }
        // Si el popup ya estaba abierto, lo cerramos
        layer.closePopup()
      })
    }

    const range = metric.range
    const min = range && range[0] !== undefined ? range[0] : 0
    const max = range && range[1] !== undefined ? range[1] : 100
    const mid = (min + max) / 2

    return (
      <div className="leaflet-map-wrapper" style={{ width: '100%', height: '600px', position: 'relative' }}>
        <MapContainer
          center={[40.4, -3.7]}
          zoom={6}
          style={{ width: '100%', height: '100%', zIndex: 1 }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
            url={`https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${process.env.REACT_APP_CARTO_BASEMAPS_API_KEY}`}
            subdomains="abcd"
            maxZoom={19}
          />
          <GeoJSON 
            key={`geojson-${metric.id}-${year?.name || 'initial'}-${datasetSignature(metricData as any[])}`}
            data={geojsonData} 
            style={style}
            onEachFeature={onEachFeature}
          />


          <MapController geojsonData={geojsonData} />
          <MapInstanceHandler onMapReady={setMapInstance} />
        </MapContainer>

        {/* Legend */}
        <div style={{
          position: 'absolute',
          bottom: '20px',
          right: '20px',
          zIndex: 1000,
          background: 'white',
          padding: '12px',
          borderRadius: '6px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
          width: '260px'
        }}>
          {metric.unit && (
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '8px', textAlign: 'left' }}>
              {metric.unit}
            </div>
          )}
          <div style={{
            height: '12px',
            width: '100%',
            background: 'linear-gradient(to right, #d32f2f 0%, #fff176 50%, #1976d2 100%)',
            borderRadius: '4px',
            marginBottom: '8px'
          }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#555', fontFamily: 'sans-serif' }}>
            <span>{formatLegendNumber(min)}</span>
            <span>{formatLegendNumber(mid)}</span>
            <span>{formatLegendNumber(max)}</span>
          </div>
        </div>
      </div>
    )
  }
)
